import React, { useState, useEffect } from 'react';
import 'bootstrap/dist/css/bootstrap.min.css';
import { Helmet } from "react-helmet-async";
const HumanCapitalManagement = () => {
  const phrases = [
    "Workforce Analytics Matrices",
    "Automated Lifecycle Workflows",
    "Strategic Talent Orchestration"
  ];

  const cardData = [
    { type: 'navy-core', content: '👥' },
    { type: 'cobalt-wave', content: '📊' },
    { type: 'lifecycle-stack', content: '⚙️' },
    { type: 'talent-spark', content: '🎯' }
  ];

  const [currentPhraseIndex, setCurrentPhraseIndex] = useState(0);
  const [displayedText, setDisplayedText] = useState('');
  const [isDeleting, setIsDeleting] = useState(false);
  const [cardIndex, setCardIndex] = useState(0);

  useEffect(() => {
    const currentPhrase = phrases[currentPhraseIndex];
    let timer;

    const handleTyping = () => {
      if (!isDeleting) {
        setDisplayedText(currentPhrase.substring(0, displayedText.length + 1));
        if (displayedText === currentPhrase) {
          timer = setTimeout(() => setIsDeleting(true), 1600);
          return;
        }
      } else {
        setDisplayedText(currentPhrase.substring(0, displayedText.length - 1));
        if (displayedText === '') {
          setIsDeleting(false);
          setCurrentPhraseIndex((prev) => (prev + 1) % phrases.length);
          return;
        }
      }

      const speed = isDeleting ? 30 : 80;
      timer = setTimeout(handleTyping, speed);
    };

    timer = setTimeout(handleTyping, 100);
    return () => clearTimeout(timer);
  }, [displayedText, isDeleting, currentPhraseIndex]);

  useEffect(() => {
    const cardTimer = setInterval(() => {
      setCardIndex((prev) => (prev + 1) % cardData.length);
    }, 1100);
    return () => clearInterval(cardTimer);
  }, []);

  // Intersection Observer for Scroll Animations
  useEffect(() => {
    const observerOptions = {
      root: null,
      rootMargin: '0px',
      threshold: 0.15
    };

    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          entry.target.classList.add('animate-visible');
        }
      });
    }, observerOptions);

    const animatedElements = document.querySelectorAll(
      '.scroll-anim-fade-in, .scroll-anim-fade-in-up, .scroll-anim-fade-in-left, .scroll-anim-fade-in-right, .scroll-anim-flip, .scroll-anim-zoom-in'
    );

    animatedElements.forEach((el) => observer.observe(el));

    return () => {
      animatedElements.forEach((el) => observer.unobserve(el));
    };
  }, []);

  // Content Data
  const coreServices = [
    { icon: "👥", title: "Core HR & Global Employee Records", desc: "Establish a single, reliable repository for global employee data, organization charts, cross-border compliance mapping, and personal profiles." },
    { icon: "📊", title: "Automated Payroll & Compensation Grids", desc: "Deploy hyper-accurate multi-currency calculation systems that automatically sync local tax rates, benefits data, and hours worked." },
    { icon: "🎯", title: "Talent Acquisition & Lifecycle Tracks", desc: "Build streamlined applicant pipeline trackers that manage the entire candidate process from candidate sourcing to automated electronic contracts." },
    { icon: "⚡", title: "Continuous Performance & Goal Tracing", desc: "Replace slow yearly evaluation reviews with continuous checking systems, balanced scorecards, and cross-team goal mapping loops." },
    { icon: "🎓", title: "Learning Management & Skill Spaces", desc: "Coordinate custom online educational portals that deliver personalized role training, compliance milestones, and verified skill certifications." },
    { icon: "🔄", title: "Onboarding & Offboarding Pipelines", desc: "Construct rule-based automation workflows to automatically issue equipment requests, unlock network security, and verify mandatory compliance signings." },
    { icon: "🛡️", title: "Time Tracking & Absence Compliance", desc: "Integrate self-service scheduling request engines that validate time-off accruals against regional labor rules automatically." }
  ];

  const advancedServices = [
    { icon: "⏳", title: "Predictive Attrition Risk Modeling", desc: "Deploy statistical analytical algorithms that evaluate behavioral engagement signs to flag early flight risks before losses happen." },
    { icon: "🔍", title: "Strategic Workforce Demand Forecasting", desc: "Run predictive operational resource simulations to align future open headcount models directly against expected long-term company budgets." },
    { icon: "💼", title: "Succession Optimization Matrix", desc: "Map leadership succession tracks programmatically across global groups to isolate internal operational readiness gaps." },
    { icon: "⚖️", title: "Equal Opportunity Pay Equity Auditing", desc: "Incorporate automated statistical check modules to continuously monitor pay variances across roles to pass statutory compliance reviews." },
    { icon: "🚨", title: "Custom Employee Sentiment Trackers", desc: "Deploy pulse feedback collectors that evaluate overall team climate indices automatically across distributed remote teams." },
    { icon: "🗃️", title: "Flexible Total Benefits Customization", desc: "Unify external insurance packages, retirement accounts, and corporate perks into one simple portal layer." },
    { icon: "🔗", title: "Cross-Platform ERP System Bridges", desc: "Synchronize live headcount resource expenditures transparently into standard centralized accounting software structures." },
    { icon: "📝", title: "Digital Security Verification Hubs", desc: "Manage background confirmation screening records and internal workplace policy agreements inside secure, unalterable file safes." },
    { icon: "🛠️", title: "Mobile Employee Self-Service Apps", desc: "Empower distributed shift employees to view payroll distribution sheets and adjust emergency contact files instantly from any device." }
  ];

  const technologies = {
    "Core Enterprise Suites": ["Workday Core Human Capital", "SAP SuccessFactors Network", "Oracle Fusion HCM Architecture", "BambooHR / HiBob Platform Hubs"],
    "Talent Ingestion & ATS": ["Greenhouse API Systems", "Lever Sourcing Pipelines", "iCIMS Candidate Systems", "SmartRecruiters Match Engines"],
    "Analytics & Data Bridges": ["Visier People Analytics Blocks", "Microsoft PowerBI Connectors", "Tableau Labor Dashboards", "Alteryx Data Wrangling Layers"],
    "Payroll & Compliance Tech": ["ADP Vantage Integration", "Deel Global Payroll Matrices", "Rippling System Unifiers", "Gusto Developer APIs"],
    "Verification & Feedback": ["Qualtrics Employee Experience", "Checkr API Background Screening", "DocuSign Secure Signing Modules", "Lattice Performance Frameworks"]
  };

  const processSteps = [
    { num: "01", title: "Operational Landscape Audit", desc: "Profiling existing data field configurations, auditing broken data silos, and setting compliance parameters." },
    { num: "02", title: "Architecture Architecture Design", desc: "Structuring central data schema profiles, isolating security clearance groups, and mapping custom approval gates." },
    { num: "03", title: "Data Ingestion & Migration", desc: "Cleaning legacy database records, testing structural transform routines, and importing unified historical data." },
    { num: "04", title: "System Rule Integration", desc: "Programming conditional absence tracking formulas, global tax calculations, and dynamic role hierarchies." },
    { num: "05", title: "Security Testing & Validation", desc: "Executing complete role-access check sequences, payroll edge verification tests, and platform load reviews." },
    { num: "06", title: "Phased Deployment Live Launch", desc: "Provisioning user profiles across global operational clusters with active single sign-on (SSO) validation layers." },
    { num: "07", title: "Operational Optimization Loop", desc: "Reviewing workflow processing bottlenecks, refining self-service routing layers, and updating system configuration matrices." }
  ];

  const industries = [
    "High-Growth Global Technology Enterprises", "Multi-Location Retail & Consumer Networks", "Cross-Border Professional Service Advisory",
    "High-Density Manufacturing & Logistics Groups", "Healthcare Facilities & Clinical Systems", "Financial Banking & FinTech Corporations",
    "Energy Infrastructure & Field Utilities", "Hospitality, Food Service & Entertainment Brands", "Public Sector Government Administration",
    "Biotech, Pharma & Medical Research Units", "Media, Advertising & Creative Agencies", "Higher Education & Multi-Campus Systems"
  ];

  const designBenefits = [
    "Zero Headcount Reconciliation Discrepancies", "Up to 60% Drop in Manual HR Task Durations", "Complete Global Regulatory Labor Compliance",
    "Drastic Reductions in Manual Data Entry Flaws", "Significant Increases in Talent Retention Metrics", "Highly Streamlined Onboarding Processing Rates",
    "Total Structural Scaling Elasticity Options", "100% Secure, Transparent Digital Audit Footprints"
  ];

  const whyChooseUs = [
    "Certified Enterprise HCM Architecture Engineers", "Proven Record in Global Enterprise Migrations", "Airtight SOC2 & Data Privacy Protection",
    "Deep Mastery in Complex Compensation Logic", "Platform-Agnostic Hybrid System Integrations", "Resilient Automation Workflow Configurations",
    "End-to-End Core Lifecycle Transformation Ownership", "Transparent Project Outcome Metric Frameworks", "High-Availability Support Maintenance Agreements",
    "Experienced Workforce Analytics System Designers", "Iterative Prototype Validation Sprints", "100% Platform Stability Delivery Assurances"
  ];

  return (
    <>

      <Helmet>
        {/* Primary SEO */}
        <title>
          Human Capital Management (HCM) Services | HR Technology Solutions | Affsoft IT Solutions Pvt. Ltd.
        </title>

        <meta
          name="title"
          content="Human Capital Management (HCM) Services | HR Technology Solutions | Affsoft IT Solutions Pvt. Ltd."
        />

        <meta
          name="description"
          content="Affsoft IT Solutions Pvt. Ltd. provides Human Capital Management (HCM) services, including HR consulting, payroll management, talent acquisition, workforce management, employee engagement, HR automation, performance management, and cloud-based HCM solutions."
        />

        <meta
          name="keywords"
          content="Human Capital Management, HCM Services, Human Resource Management, HR Technology, HR Consulting, Payroll Management, Talent Management, Talent Acquisition, Workforce Management, Employee Engagement, Performance Management, HR Automation, Cloud HCM, HR Software Solutions, Enterprise HCM, Digital HR Transformation, Human Capital Solutions, Affsoft IT Solutions Pvt. Ltd."
        />

        <meta name="robots" content="index, follow" />
        <meta name="author" content="Affsoft IT Solutions Pvt. Ltd." />
        <meta name="language" content="English" />
        <meta name="theme-color" content="#0d5c53" />

        {/* Canonical */}
        <link
          rel="canonical"
          href="https://www.affsoft.com/human-capital-management"
        />

        {/* Open Graph */}
        <meta property="og:type" content="website" />
        <meta
          property="og:site_name"
          content="Affsoft IT Solutions Pvt. Ltd."
        />
        <meta
          property="og:title"
          content="Professional Human Capital Management (HCM) Services | Affsoft IT Solutions Pvt. Ltd."
        />
        <meta
          property="og:description"
          content="Empower your workforce with Human Capital Management (HCM) services from Affsoft IT Solutions Pvt. Ltd. We provide HR consulting, payroll management, talent acquisition, workforce management, employee engagement, and cloud HCM solutions."
        />
        <meta
          property="og:url"
          content="https://www.affsoft.com/human-capital-management"
        />
        <meta
          property="og:image"
          content="https://www.affsoft.com/images/affsoft-logo.png"
        />

        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta
          name="twitter:title"
          content="Human Capital Management (HCM) Services | HR Technology Solutions | Affsoft IT Solutions Pvt. Ltd."
        />
        <meta
          name="twitter:description"
          content="Optimize your HR operations with Human Capital Management services from Affsoft IT Solutions Pvt. Ltd. We deliver HR automation, payroll, talent management, workforce optimization, and cloud-based HCM solutions."
        />
        <meta
          name="twitter:image"
          content="https://www.affsoft.com/images/affsoft-logo.png"
        />
      </Helmet>
      <div className="hcm-services-page bg-light" style={{ fontFamily: '"Inter", sans-serif', overflowX: 'hidden' }}>

        {/* ================= HERO SECTION ================= */}
        <div
          className="hero-container container-fluid d-flex align-items-center justify-content-center position-relative overflow-hidden"
          style={{
            backgroundImage: "url('./images/home.jpg')",
            backgroundRepeat: 'no-repeat',
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            minHeight: '85vh',
            backgroundColor: '#0f172a' // Deep navy slate fallback
          }}
        >
          {/* Background Grid Lines */}
          <div className="grid-overlay position-absolute top-0 start-0 w-100 h-100" style={{ opacity: 0.12 }}>
            <div className="grid-line line-v-1"></div>
            <div className="grid-line line-v-2"></div>
            <div className="grid-line line-v-3"></div>
          </div>

          {/* Infinite Slow-Scrolling Marquee Text Background Wrapper */}
          <div className="marquee-wrapper select-none position-absolute w-100 scroll-anim-fade-in" style={{ top: '15%', opacity: 0.05 }}>
          </div>

          <div className="marquee-wrapper select-none position-absolute w-100 scroll-anim-fade-in">
            <div className="marquee-content text-white">
              <span>Human &nbsp; Capital &nbsp; Management &nbsp; &amp; &nbsp;</span>
              <span>Workforce Transformation Platforms </span>
            </div>
          </div>

          {/* Main Content Layout */}
          <div className="container position-relative z-3 my-5">
            <div className="row align-items-center py-4">

              {/* Left Side Info */}
              <div className="col-12 col-md-5 text-start d-flex flex-column align-items-start hero-left-pane text-white mb-4 mb-md-0 scroll-anim-fade-in-left">
                <div className="asterisk-icon mb-3 text-cobalt fs-3" style={{ color: '#3b82f6' }}>✳</div>
                <h1 className="fw-bold mb-3 display-5">Human Capital Management</h1>
                <p className="tagline mb-4 text-white" style={{ fontSize: '1rem', lineHeight: '1.6', opacity: 0.9 }}>
                  At Affsoft IT Solutions Pvt. Ltd., we eliminate the operational fragmentation that limits organizational scale. Our human capital management engineering practice unifies distributed core HR master data models with automated life cycle tracks and intelligent people analytics to turn raw labor metrics into strategic development engines.
                </p>
                <button className="btn btn-outline-light d-flex align-items-center gap-2 px-4 py-2 rounded-pill fw-semibold">
                  <span>➔</span>
                  <span>Propose an HCM Blueprint</span>
                </button>
              </div>

              {/* Center Section */}
              <div className="col-12 col-md-4 text-center mb-4 mb-md-0 hero-center-pane scroll-anim-flip">
                <div className="typing-badge px-4 py-3 mx-auto bg-dark bg-opacity-75 rounded-pill border border-secondary shadow-lg d-inline-block">
                  <span className="typed-text text-white fw-mono fs-4">{displayedText}</span>
                  <span className="cursor fs-4 animate-blink" style={{ color: '#3b82f6' }}>|</span>
                </div>
              </div>

              {/* Right Section */}
              <div className="col-12 col-md-3 d-flex justify-content-center justify-content-md-end hero-right-pane scroll-anim-fade-in-right">
                <div className="glass-card-frame shadow-lg p-3 bg-white bg-opacity-10 rounded-4 border border-light" style={{ width: '220px', backdropFilter: 'blur(10px)' }}>
                  <div className={`inner-graphic-card graphic-${cardData[cardIndex].type} d-flex align-items-center justify-content-center rounded-3 bg-dark`} style={{ height: '180px' }}>
                    <span className="graphic-icon display-3">{cardData[cardIndex].content}</span>
                  </div>
                </div>
              </div>

            </div>
          </div>
        </div>

        {/* ================= CORE SOLUTIONS SECTION ================= */}
        <section className="py-5 bg-white">
          <div className="container">
            <div className="text-center max-width-700 mx-auto mb-5 scroll-anim-fade-in-up">
              <span className="badge px-3 py-2 rounded-pill mb-2 text-uppercase text-white" style={{ backgroundColor: '#3b82f6' }}>Workforce System Tuning</span>
              <h2 className="fw-bold text-dark display-6">Deploy Resilient Talent Architecture & Automated Lifecycle Pipelines</h2>
              <p className="text-muted mt-3">
                Disconnected regional tracking siloes, fragile manual compensation processing steps, and lack of visibility into data patterns stall modern workforce growth strategies. At Affsoft IT Solutions Pvt. Ltd., we construct secure, robust, and completely integrated enterprise management infrastructure.
              </p>
            </div>

            <div className="row g-4">
              {coreServices.map((service, index) => (
                <div className="col-12 col-md-6 col-lg-4 scroll-anim-zoom-in" key={index} style={{ animationDelay: `${index * 0.1}s` }}>
                  <div className="card h-100 shadow-sm border-0 rounded-4 p-4 hover-translate transition-all">
                    <div className="fs-1 mb-3" style={{ color: '#3b82f6' }}>{service.icon}</div>
                    <h4 className="fw-bold fs-5 mb-2 text-dark">{service.title}</h4>
                    <p className="text-muted mb-0 small">{service.desc}</p>
                  </div>
                </div>
              ))}
            </div>

            {/* Business Value Checklist */}
            <div className="mt-5 p-4 rounded-4 bg-light border scroll-anim-fade-in-up">
              <h5 className="fw-bold text-center text-dark mb-4">Core Operational Gains of Strategic Enterprise HCM Upgrades</h5>
              <div className="row g-3">
                {designBenefits.map((benefit, index) => (
                  <div className="col-6 col-md-3 d-flex align-items-center gap-2" key={index}>
                    <span className="text-success fs-5">✓</span>
                    <span className="text-secondary small fw-medium">{benefit}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* ================= ADVANCED SOLUTIONS OPTIONS ================= */}
        <section className="py-5 bg-light">
          <div className="container">
            <div className="text-center max-width-700 mx-auto mb-5 scroll-anim-fade-in-up">
              <span className="badge px-3 py-2 rounded-pill mb-2 text-uppercase text-white" style={{ backgroundColor: '#3b82f6' }}>Intelligent Talent Optimization</span>
              <h2 className="fw-bold text-dark display-6">Adaptive Administrative Capabilities Built for Global Scale</h2>
              <p className="text-muted mt-3">
                We scale standard static administrative records into modern self-adjusting organizational systems. From automated retention hazard scoring models to real-time payroll data sync pathways, our tools keep employee operations stable.
              </p>
            </div>

            <div className="row g-4">
              {advancedServices.map((service, index) => (
                <div className="col-12 col-md-6 col-lg-4 scroll-anim-zoom-in" key={index} style={{ animationDelay: `${index * 0.1}s` }}>
                  <div className="card h-100 shadow-sm border-0 rounded-4 p-4 bg-white hover-translate transition-all">
                    <div className="fs-1 mb-3" style={{ color: '#3b82f6' }}>{service.icon}</div>
                    <h4 className="fw-bold fs-5 mb-2 text-dark">{service.title}</h4>
                    <p className="text-muted mb-0 small">{service.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ================= TECHNOLOGIES SECTION ================= */}
        <section className="py-5 bg-dark text-white">
          <div className="container">
            <div className="text-center mb-5 scroll-anim-fade-in-up">
              <span className="badge px-3 py-2 rounded-pill mb-2 text-uppercase fw-bold text-dark" style={{ backgroundColor: '#60a5fa' }}>Enterprise Tool Matrix</span>
              <h2 className="fw-bold display-6">HCM Infrastructure & Workforce Systems We Unify</h2>
              <p className="text-light-50">We structure, interface, and maintain enterprise organizational platforms using leading cloud software toolsets.</p>
            </div>

            <div className="row g-4 justify-content-center">
              {Object.entries(technologies).map(([category, list], index) => (
                <div className="col-12 col-sm-6 col-md-4 col-lg scroll-anim-fade-in" key={index}>
                  <div className="bg-secondary bg-opacity-25 p-4 rounded-4 h-100 border border-secondary border-opacity-50">
                    <h5 className="fw-bold mb-3 border-bottom border-secondary pb-2" style={{ color: '#60a5fa' }}>{category}</h5>
                    <ul className="list-unstyled mb-0">
                      {list.map((tech, i) => (
                        <li key={i} className="mb-2 d-flex align-items-center gap-2 small">
                          <span className="opacity-75" style={{ color: '#60a5fa' }}>•</span> {tech}
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ================= ROADMAP PROCESS ================= */}
        <section className="py-5 bg-white">
          <div className="container">
            <div className="text-center mb-5 scroll-anim-fade-in-up">
              <span className="badge bg-secondary px-3 py-2 rounded-pill mb-2 text-uppercase">Platform Lifecycle</span>
              <h2 className="fw-bold text-dark display-6">Our Engineered Integration Architecture</h2>
              <p className="text-muted">A clear, highly disciplined implementation roadmap designed to guide critical workforce platforms safely from initial analysis down to global automated rollouts.</p>
            </div>

            <div className="row g-3">
              {processSteps.map((step, index) => (
                <div className="col-12 col-md-6 col-lg scroll-anim-fade-in-up" key={index} style={{ animationDelay: `${index * 0.15}s` }}>
                  <div className="p-4 rounded-4 bg-light border-start border-4 h-100 shadow-sm" style={{ borderLeftColor: '#3b82f6' }}>
                    <div className="fw-bold display-6 mb-2" style={{ color: '#3b82f6' }}>{step.num}</div>
                    <h5 className="fw-bold text-dark fs-6">{step.title}</h5>
                    <p className="text-muted small mb-0">{step.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ================= INDUSTRIES SERVED ================= */}
        <section className="py-5 bg-light">
          <div className="container text-center">
            <h2 className="fw-bold text-dark mb-4 display-6 scroll-anim-fade-in-up">Ecosystems We Empower</h2>
            <div className="d-flex flex-wrap justify-content-center gap-2 max-width-900 mx-auto scroll-anim-zoom-in">
              {industries.map((industry, index) => (
                <span className="badge bg-white text-dark border px-3 py-2 rounded-pill shadow-sm fs-7 fw-medium" key={index}>
                  🏢 {industry}
                </span>
              ))}
            </div>
          </div>
        </section>

        {/* ================= WHY CHOOSE AFFSOFT ================= */}
        <section className="py-5 bg-white border-top">
          <div className="container">
            <div className="text-center mb-5 scroll-anim-fade-in-up">
              <h2 className="fw-bold text-dark display-6">Why Partner with Affsoft IT Solutions Pvt. Ltd.?</h2>
              <p className="text-muted">We combine strict target field mappings with modern data governance to guarantee clean integration performance across global instances.</p>
            </div>

            <div className="row g-3">
              {whyChooseUs.map((reason, index) => (
                <div className="col-12 col-md-6 col-lg-4 scroll-anim-fade-in-left" key={index} style={{ animationDelay: `${(index % 3) * 0.1}s` }}>
                  <div className="d-flex align-items-center gap-3 p-3 bg-light rounded-3 h-100">
                    <span className="fs-4" style={{ color: '#3b82f6' }}>🛡️</span>
                    <span className="text-dark fw-semibold small">{reason}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Custom Styles Injection */}
        <style>{`
        .hover-translate {
          transition: transform 0.2s ease, box-shadow 0.2s ease;
        }
        .hover-translate:hover {
          transform: translateY(-5px);
          box-shadow: 0 10px 20px rgba(0,0,0,0.1) !important;
        }
        .text-light-50 {
          color: rgba(255, 255, 255, 0.7);
        }
        .max-width-700 {
          max-width: 700px;
        }
        .max-width-900 {
          max-width: 900px;
        }
        .animate-blink {
          animation: blink 1s step-end infinite;
        }
        @keyframes blink {
          from, to { color: transparent }
          50% { color: inherit }
        }

        /* --- Scroll Animation Core --- */
        .scroll-anim-fade-in,
        .scroll-anim-fade-in-up,
        .scroll-anim-fade-in-left,
        .scroll-anim-fade-in-right,
        .scroll-anim-flip,
        .scroll-anim-zoom-in {
          opacity: 0;
          will-change: transform, opacity;
          transition: all 0.8s cubic-bezier(0.25, 0.46, 0.45, 0.94);
        }

        /* Visibility Trigger via IntersectionObserver */
        .animate-visible {
          opacity: 1 !important;
          transform: none !important;
        }

        /* --- Animations Configurations --- */
        .scroll-anim-fade-in-up {
          transform: translateY(40px);
        }
        .scroll-anim-fade-in-left {
          transform: translateX(-50px);
        }
        .scroll-anim-fade-in-right {
          transform: translateX(50px);
        }
        .scroll-anim-zoom-in {
          transform: scale(0.85);
        }
        .scroll-anim-flip {
          transform: perspective(400px) rotateY(90deg);
          transition: all 1s cubic-bezier(0.175, 0.885, 0.32, 1.275);
        }
        
        .scroll-anim-flip.animate-visible {
          transform: perspective(400px) rotateY(0deg) !important;
        }
      `}</style>

      </div>

    </>
  );
};

export default HumanCapitalManagement;